import { moveToFulfilled, update } from '@ms/yammer-data-support';
import type { Reducer } from 'redux';

import type {
  AllCampaignsLoadFulfilledAction,
  CampaignsListLoadMoreFulfilledAction,
} from '../../domains/allCampaigns/actions.types';
import type { CampaignCreateFulfilledAction } from '../../domains/campaignCreation/actions.types';
import type { CampaignDeleteFulfilledAction } from '../../domains/campaignDeletion/actions.types';
import type {
  CampaignEditFulfilledAction,
  CampaignPublishFulfilledAction,
} from '../../domains/campaignEdit/actions.types';
import type {
  CampaignFollowFulfilledAction,
  CampaignUnfollowFulfilledAction,
} from '../../domains/campaignFollowing/actions.types';
import type { CampaignSashAddFulfilledAction } from '../../domains/campaignSash/addSashActions.types';
import type { CampaignSashRemoveFulfilledAction } from '../../domains/campaignSash/removeSashActions.types';
import type { CampaignLoadFulfilledAction } from '../../domains/campaigns/actions.types';
import type { CreateEventFulfilledAction } from '../../domains/createEvent/actions.types';
import type { CuratedThreadListLoadAction } from '../../domains/curatedThreadList/actions.types';
import type { DelegationTeamsLoadFulfilledAction } from '../../domains/delegation/actions.types';
import type { EditEventFulfilledAction } from '../../domains/editEvent/actions.types';
import type { EmployeesTopThreadsLoadFulfilledAction } from '../../domains/employeesTopThreads/actions.types';
import type { EventLoadByBroadcastIdFulfilledAction } from '../../domains/eventLoadByBroadcastId/actions.types';
import type {
  FeedLoadFulfilledAction,
  FeedLoadNewerThreadsFulfilledAction,
  FeedLoadOlderThreadsFulfilledAction,
} from '../../domains/feedLoad/actions.types';
import type { GroupAddUserByEmailFulfilledAction } from '../../domains/groupAddRemoveUser/addUserByEmailActions.types';
import type {
  GroupMemberPanelsLoadFulfilledAction,
  GroupMemberPanelsLoadMoreFulfilledAction,
} from '../../domains/groupMemberPanels/actions.types';
import type { GroupMemberSearchLoadFulfilledAction } from '../../domains/groupMemberSearch/actions.types';
import type { GroupPublisherSettingsFulfilledAction } from '../../domains/groupPublisherSettings/actions.types';
import type { GroupRoleMembersFulfilledAction } from '../../domains/groupRoleMembers/actions.types';
import type { GroupSidebarLoadFulfilledAction } from '../../domains/groupSidebarLoad/actions.types';
import type { GroupCreateFulfilledAction, GroupEditFulfilledAction } from '../../domains/groups/actions.types';
import type { GroupsMembersLoadFulfilledAction } from '../../domains/groupsMembers/actions.types';
import type {
  AudienceMembersLoadFulfilledAction,
  LeaderLoadFulfilledAction,
  NewLeadersSuggestionsLoadFulfilledAction,
  UserConnectedLeadersListLoadFulfilledAction,
  UserConnectedLeadersListLoadMoreFulfilledAction,
  UserFollowedLeadersListLoadFulfilledAction,
  UserFollowedLeadersListLoadMoreFulfilledAction,
  UserNetworkLeadersListLoadFulfilledAction,
  UserNetworkLeadersListLoadMoreFulfilledAction,
} from '../../domains/leadersManagement/actions.types';
import type { LiveEventLoadByGroupIdFulfilledAction } from '../../domains/liveEventLoadByGroupId/actions.types';
import type {
  M365CopilotSuggestedMembersLoadFulfilledAction,
  M365CopilotSuggestedMembersLoadMoreFulfilledAction,
} from '../../domains/m365CopilotGroupSuggestedMembers/actions.types';
import type { MessageLanguageSpecificContentLoadAction } from '../../domains/messageLanguageSpecificContent/actions.types';
import type { MessageReactionsLoadFulfilledAction } from '../../domains/messageReactions/actions.types';
import type {
  MessageUpvotesLoadFulfilledAction,
  MessageUpvotesLoadMoreFulfilledAction,
} from '../../domains/messageUpvoteLoad/actions.types';
import type { MyLeaderSponsoredCampaignsLoadFulfilledAction } from '../../domains/myLeaderSponsoredCampaigns/loadActions.types';
import type {
  NestedThreadDeepLinkSecondLevelLoadFulfilledAction,
  NestedThreadDeepLinkTopLevelLoadFulfilledAction,
  NestedThreadLoadFulfilledAction,
} from '../../domains/nestedThread/types';
import type { NestedThreadListLoadFulfilledAction } from '../../domains/nestedThreadList/actions.types';
import type {
  OfficialGroupsLoadFulfilledAction,
  OfficialGroupsLoadMoreFulfilledAction,
} from '../../domains/officialGroups/actions.types';
import type {
  ThreadPinToEventFeedFulfilledAction,
  ThreadPinToGroupFeedFulfilledAction,
  ThreadPinToTeamsMeetingFeedFulfilledAction,
  ThreadUnpinFromEventFeedFulfilledAction,
  ThreadUnpinFromGroupFeedFulfilledAction,
  ThreadUnpinFromTeamsMeetingFeedFulfilledAction,
} from '../../domains/pinnedThreads/actions.types';
import type {
  PublishDirectMessageFulfilledAction,
  PublishEditMessageFulfilledAction,
  PublishEventMessageFulfilledAction,
  PublishGroupMessageFulfilledAction,
  PublishPollMessageFulfilledAction,
  PublishPraiseMessageFulfilledAction,
  PublishReplyMessageFulfilledAction,
  PublishTeamsMeetingMessageFulfilledAction,
  PublishUserMessageFulfilledAction,
  PublishUserPollMessageFulfilledAction,
  PublishUserPraiseMessageFulfilledAction,
} from '../../domains/publishers/actions.types';
import type {
  RecommendedGroupsLoadFulfilledAction,
  RecommendedGroupsLoadMoreFulfilledAction,
} from '../../domains/recommendedGroups/actions.types';
import type { SearchLoadFulfilledAction, SearchLoadMoreFulfilledAction } from '../../domains/searches/actions.types';
import type {
  SecondLevelRepliesLoadLastFulfilledAction,
  SecondLevelRepliesLoadNextFulfilledAction,
  SecondLevelRepliesLoadPreviousFulfilledAction,
} from '../../domains/secondLevelReplies/actions.types';
import type { SessionLoadCurrentUserFulfilledAction } from '../../domains/sessionLoadCurrentUser/actions.types';
import type { SuggestedActionsLoadFulfilledAction } from '../../domains/suggestedActions/actions.types';
import type {
  SuggestedGroupsLoadFulfilledAction,
  SuggestedGroupsLoadMoreFulfilledAction,
} from '../../domains/suggestedGroups/actions.types';
import type {
  SuggestedLeadersLoadFulfilledAction,
  SuggestedLeadersLoadMoreFulfilledAction,
} from '../../domains/suggestedLeaders/actions.types';
import type { SuggestedMyLeaderGroupsLoadFulfilledAction } from '../../domains/suggestedMyLeaderGroups/actions.types';
import type {
  SuggestedUsersLoadFulfilledAction,
  SuggestedUsersLoadMoreFulfilledAction,
} from '../../domains/suggestedUsers/actions.types';
import type { TeamsMeetingDelegationPermissionsLoadAction } from '../../domains/teamsMeetingDelegationPermissions/actions.types';
import type { TeamsMeetingLoadFulfilledAction } from '../../domains/teamsMeetingLoad/actions.types';
import type { TeamsMeetingsByOrganizerFulfilledAction } from '../../domains/teamsMeetingsByOrganizer/actions.types';
import type { ThreadSharesLoadFulfilledAction } from '../../domains/threadShares/actions.types';
import type {
  TopLevelRepliesLoadInitialFulfilledAction,
  TopLevelRepliesLoadLastFulfilledAction,
  TopLevelRepliesLoadNextFulfilledAction,
  TopLevelRepliesLoadPreviousFulfilledAction,
} from '../../domains/topLevelReplies/actions.types';
import type { TopicFollowersLoadFulfilledAction } from '../../domains/topicFollowers/loadActions.types';
import type { TopicFollowersLoadMoreFulfilledAction } from '../../domains/topicFollowers/paginateActions.types';
import type { TrendingCampaignsLoadFulfilledAction } from '../../domains/trendingCampaigns/loadActions.types';
import type { TypeaheadFulfilledAction } from '../../domains/typeahead/actions.types';
import type { UserFollowedByLoadFulfilledAction } from '../../domains/userFollowedBy/actions.types';
import type { UserFollowedUsersLoadFulfilledAction } from '../../domains/userFollowedUsers/actions.types';
import type { UserHeaderLoadFulfilledAction } from '../../domains/userHeaderLoad/actions.types';
import type { UserLoadFulfilledAction } from '../../domains/userLoad/actions.types';
import type { UsersLoadFulfilledAction } from '../../domains/usersLoad/actions.types';

import type { Users } from './types';
export type UserAction =
  | CampaignEditFulfilledAction
  | CampaignPublishFulfilledAction
  | CampaignDeleteFulfilledAction
  | CampaignSashAddFulfilledAction
  | CampaignSashRemoveFulfilledAction
  | CreateEventFulfilledAction
  | EditEventFulfilledAction
  | EventLoadByBroadcastIdFulfilledAction
  | FeedLoadFulfilledAction
  | FeedLoadNewerThreadsFulfilledAction
  | FeedLoadOlderThreadsFulfilledAction
  | TeamsMeetingDelegationPermissionsLoadAction
  | ThreadPinToEventFeedFulfilledAction
  | ThreadPinToGroupFeedFulfilledAction
  | ThreadPinToTeamsMeetingFeedFulfilledAction
  | ThreadUnpinFromEventFeedFulfilledAction
  | ThreadUnpinFromGroupFeedFulfilledAction
  | ThreadUnpinFromTeamsMeetingFeedFulfilledAction
  | GroupSidebarLoadFulfilledAction
  | GroupsMembersLoadFulfilledAction
  | UserHeaderLoadFulfilledAction
  | LiveEventLoadByGroupIdFulfilledAction
  | PublishEventMessageFulfilledAction
  | PublishGroupMessageFulfilledAction
  | PublishUserMessageFulfilledAction
  | PublishUserPollMessageFulfilledAction
  | PublishUserPraiseMessageFulfilledAction
  | PublishDirectMessageFulfilledAction
  | PublishEditMessageFulfilledAction
  | PublishReplyMessageFulfilledAction
  | PublishPollMessageFulfilledAction
  | PublishPraiseMessageFulfilledAction
  | PublishTeamsMeetingMessageFulfilledAction
  | TypeaheadFulfilledAction
  | SearchLoadFulfilledAction
  | SearchLoadMoreFulfilledAction
  | SessionLoadCurrentUserFulfilledAction
  | UserLoadFulfilledAction
  | UsersLoadFulfilledAction
  | UserFollowedByLoadFulfilledAction
  | UserFollowedUsersLoadFulfilledAction
  | MessageReactionsLoadFulfilledAction
  | GroupMemberSearchLoadFulfilledAction
  | GroupMemberPanelsLoadFulfilledAction
  | GroupMemberPanelsLoadMoreFulfilledAction
  | NestedThreadLoadFulfilledAction
  | NestedThreadDeepLinkTopLevelLoadFulfilledAction
  | NestedThreadDeepLinkSecondLevelLoadFulfilledAction
  | NestedThreadListLoadFulfilledAction
  | TeamsMeetingLoadFulfilledAction
  | TeamsMeetingsByOrganizerFulfilledAction
  | TopLevelRepliesLoadInitialFulfilledAction
  | TopLevelRepliesLoadLastFulfilledAction
  | TopLevelRepliesLoadPreviousFulfilledAction
  | TopLevelRepliesLoadNextFulfilledAction
  | SecondLevelRepliesLoadPreviousFulfilledAction
  | SecondLevelRepliesLoadNextFulfilledAction
  | GroupPublisherSettingsFulfilledAction
  | GroupCreateFulfilledAction
  | GroupEditFulfilledAction
  | DelegationTeamsLoadFulfilledAction
  | GroupAddUserByEmailFulfilledAction
  | RecommendedGroupsLoadFulfilledAction
  | RecommendedGroupsLoadMoreFulfilledAction
  | SuggestedActionsLoadFulfilledAction
  | SuggestedGroupsLoadFulfilledAction
  | SuggestedGroupsLoadMoreFulfilledAction
  | SuggestedLeadersLoadFulfilledAction
  | SuggestedLeadersLoadMoreFulfilledAction
  | SuggestedUsersLoadFulfilledAction
  | SuggestedUsersLoadMoreFulfilledAction
  | MessageUpvotesLoadFulfilledAction
  | MessageUpvotesLoadMoreFulfilledAction
  | SecondLevelRepliesLoadLastFulfilledAction
  | ThreadSharesLoadFulfilledAction
  | TopicFollowersLoadFulfilledAction
  | TopicFollowersLoadMoreFulfilledAction
  | UserConnectedLeadersListLoadFulfilledAction
  | UserConnectedLeadersListLoadMoreFulfilledAction
  | UserFollowedLeadersListLoadFulfilledAction
  | UserFollowedLeadersListLoadMoreFulfilledAction
  | UserNetworkLeadersListLoadFulfilledAction
  | UserNetworkLeadersListLoadMoreFulfilledAction
  | LeaderLoadFulfilledAction
  | SuggestedMyLeaderGroupsLoadFulfilledAction
  | CampaignLoadFulfilledAction
  | AllCampaignsLoadFulfilledAction
  | CampaignsListLoadMoreFulfilledAction
  | MyLeaderSponsoredCampaignsLoadFulfilledAction
  | EmployeesTopThreadsLoadFulfilledAction
  | OfficialGroupsLoadFulfilledAction
  | OfficialGroupsLoadMoreFulfilledAction
  | TrendingCampaignsLoadFulfilledAction
  | CampaignFollowFulfilledAction
  | CampaignUnfollowFulfilledAction
  | CampaignCreateFulfilledAction
  | CuratedThreadListLoadAction
  | NewLeadersSuggestionsLoadFulfilledAction
  | AudienceMembersLoadFulfilledAction
  | M365CopilotSuggestedMembersLoadFulfilledAction
  | M365CopilotSuggestedMembersLoadMoreFulfilledAction
  | GroupRoleMembersFulfilledAction
  | MessageLanguageSpecificContentLoadAction;
const initialState: Users = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};
const addUserSash = (state: Users, { userId: id, entities }: CampaignSashAddFulfilledAction['payload']) => {
  const { campaignId: sashCampaignId, sashImageUrl } = entities.userSashes[id];

  return update(state, id, {
    sashCampaignId,
    sashImageUrl,
  });
};
const removeUserSash = (state: Users, id: string, campaignId: string) => {
  const user = state.fulfilled.byId[id];
  if (user?.sashCampaignId && campaignId === user.sashCampaignId) {
    return update(state, id, {
      sashCampaignId: undefined,
      sashImageUrl: undefined,
    });
  }

  return state;
};
const removeEndCampaignSash = (
  state: Users,
  {
    id: campaignId,
    userId,
    entities,
  }: CampaignEditFulfilledAction['payload'] | CampaignPublishFulfilledAction['payload']
) => {
  const campaignReferences = entities.campaignReferences[campaignId];
  if (campaignReferences?.state === 'CLOSED') {
    return removeUserSash(state, userId, campaignId);
  }

  return state;
};
const unfollowCampaign = (
  state: Users,
  { userId, id: campaignId, entities }: CampaignUnfollowFulfilledAction['payload']
) => {
  const stateWithoutSash = removeUserSash(state, userId, campaignId);

  return moveToFulfilled(stateWithoutSash, entities.users);
};
// eslint-disable-next-line max-lines-per-function
const reducer: Reducer<Users, UserAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'CAMPAIGN.EDIT_FULFILLED':
      return removeEndCampaignSash(state, action.payload);
    case 'CAMPAIGN.PUBLISH_FULFILLED':
      return removeEndCampaignSash(state, action.payload);
    case 'CAMPAIGN.DELETE_FULFILLED':
      return removeUserSash(state, action.payload.userId, action.payload.campaignId);
    case 'CAMPAIGN.UNFOLLOW_FULFILLED':
      return unfollowCampaign(state, action.payload);
    case 'CAMPAIGN.SASH.ADD_FULFILLED':
      return addUserSash(state, action.payload);
    case 'CAMPAIGN.SASH.REMOVE_FULFILLED':
      return removeUserSash(state, action.payload.userId, action.payload.campaignId);
    case 'EVENT.LOAD_BY_BROADCAST_ID_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'USER.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'USERS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'FEED.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'FEED.LOAD_NEWER_THREADS_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'FEED.LOAD_OLDER_THREADS_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'GROUP_SIDEBAR.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'GROUPS_MEMBERS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'USER_HEADER.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'USER_FOLLOWED_BY.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'USER_FOLLOWED_USERS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'LIVE_EVENT.LOAD_BY_GROUP_ID_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'THREAD.PIN_TO_EVENT_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'THREAD.PIN_TO_GROUP_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'THREAD.PIN_TO_TEAMS_MEETING_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'THREAD.UNPIN_FROM_EVENT_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'THREAD.UNPIN_FROM_GROUP_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'THREAD.UNPIN_FROM_TEAMS_MEETING_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'TEAMS_MEETING_DELEGATION_PERMISSIONS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'PUBLISHER.PUBLISH_EVENT_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'PUBLISHER.PUBLISH_GROUP_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'PUBLISHER.PUBLISH_USER_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'PUBLISHER.PUBLISH_USER_POLL_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'PUBLISHER.PUBLISH_USER_PRAISE_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'PUBLISHER.PUBLISH_DIRECT_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'PUBLISHER.PUBLISH_EDIT_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'PUBLISHER.PUBLISH_REPLY_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'PUBLISHER.PUBLISH_POLL_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'PUBLISHER.PUBLISH_PRAISE_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'PUBLISHER.PUBLISH_TEAMS_MEETING_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'SEARCH.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'SEARCH.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'SESSION.LOAD_CURRENT_USER_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'TYPEAHEAD.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'TEAMS_MEETINGS_BY_ORGANIZER.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'TEAMS_MEETING.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'MESSAGE_REACTIONS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'GROUP_MEMBER_SEARCH.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'GROUP_MEMBER_PANELS.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'GROUP_MEMBER_PANELS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'CREATE_EVENT.FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'EDIT_EVENT.FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'NESTED_THREAD.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'NESTED_THREAD.LOAD_DEEP_LINK_TOP_LEVEL_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'NESTED_THREAD.LOAD_DEEP_LINK_SECOND_LEVEL_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'NESTED_THREAD_LIST.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'TOP_LEVEL_REPLIES.LOAD_INITIAL_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'TOP_LEVEL_REPLIES.LOAD_PREVIOUS_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'TOP_LEVEL_REPLIES.LOAD_NEXT_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'TOP_LEVEL_REPLIES.LOAD_LAST_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'SECOND_LEVEL_REPLIES.LOAD_PREVIOUS_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'SECOND_LEVEL_REPLIES.LOAD_NEXT_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'SECOND_LEVEL_REPLIES.LOAD_LAST_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'PUBLISHER.LOAD_GROUP_PUBLISHER_SETTINGS_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'GROUP.EDIT_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'DELEGATION_TEAMS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'GROUP.ADD_USER_BY_EMAIL_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'RECOMMENDED_GROUPS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'RECOMMENDED_GROUPS.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'SUGGESTED_ACTION.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'SUGGESTED_GROUPS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'SUGGESTED_GROUPS.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'SUGGESTED_LEADERS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'SUGGESTED_LEADERS.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'SUGGESTED_USERS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'SUGGESTED_USERS.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'MESSAGE_UPVOTES.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'MESSAGE_UPVOTES.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'MY_LEADER_SPONSORED_CAMPAIGNS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'THREAD_SHARES.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'TOPIC_FOLLOWERS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'TOPIC_FOLLOWERS.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'ALL_LEADERS_IN_USERS_NETWORK.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'CONNECTED_LEADERS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'CONNECTED_LEADERS.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'FOLLOWED_LEADERS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'FOLLOWED_LEADERS.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'ALL_LEADERS_IN_USERS_NETWORK.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'LEADER.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'SUGGESTED_MY_LEADER_GROUPS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'CAMPAIGN.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'CAMPAIGN.CREATE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'CAMPAIGN.FOLLOW_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'ALL_CAMPAIGNS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'ALL_CAMPAIGNS.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'EMPLOYEES_TOP_THREADS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'OFFICIAL_GROUPS.LOAD_FULFILLED':
    case 'OFFICIAL_GROUPS.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'TRENDING_CAMPAIGNS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'CURATED_THREAD_LIST_LOAD.FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'NEW_LEADERS_SUGGESTIONS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'AUDIENCE_MEMBERS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'M365_COPILOT_SUGGESTED_MEMBERS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'M365_COPILOT_SUGGESTED_MEMBERS.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'GROUP_ROLE_MEMBERS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    case 'MESSAGE_LANGUAGE_SPECIFIC_CONTENT.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.users);
    default:
      return state;
  }
};
export default reducer;
